/* eslint-disable import/no-default-export */
import { Box, Button, ButtonGroup, HStack, IconButton, Select, Text } from '@chakra-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { MdFirstPage, MdLastPage } from 'react-icons/md';

const optionsPreset = [20, 40, 60, 80, 100];

interface Props {
  currentPage: number;
  lastPage?: number;
  total: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number) => void;
  itemsPerPage: number;
  isFetching: boolean;
  storageKey?: string;
  size?: 'default' | 'large';
  options?: number[];
}

/**
 * A reusable pagination component with page size selection and local storage persistence.
 *
 * @param currentPage The current active page.
 * @param lastPage The last page number.
 * @param total The total number of items.
 * @param onPageChange A callback function to handle page changes.
 * @param onItemsPerPageChange A callback function to handle changes in items per page.
 * @param itemsPerPage The number of items to display per page.
 * @param isFetching A boolean to indicate if data is being fetched.
 * @param storageKey A unique key to store the items per page value in local storage.
 */
const Pagination: React.FC<Props> = ({
  currentPage,
  lastPage,
  total,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPage,
  isFetching,
  storageKey,
  size = 'default',
  options,
}) => {
  const perPageItem = itemsPerPage ?? /* istanbul ignore next */ 20;
  const { t } = useTranslation('moniterByRecruiters');

  const defaultOptions = React.useMemo(() => {
    const baseOptions = options ?? optionsPreset;
    return total < 100 ? [...baseOptions, total] : baseOptions;
  }, [total, options]);

  const currentOptions = React.useMemo(
    () =>
      Array.from(new Set(defaultOptions))
        .sort((a, b) => a - b)
        .filter((o) => o <= total),
    [defaultOptions, total],
  );

  const baseItemsPerPage = React.useMemo(() => {
    const item = total ? Math.min(itemsPerPage, total) : itemsPerPage;
    /* istanbul ignore else */
    if (defaultOptions.includes(item)) {
      return item;
    }

    return defaultOptions[defaultOptions.length - 1] ?? itemsPerPage;
  }, [itemsPerPage, total, defaultOptions]);

  const calculatedLastPage = React.useMemo(() => (lastPage !== undefined ? lastPage : Math.ceil(total / baseItemsPerPage)), [
    lastPage,
    total,
    baseItemsPerPage,
  ]);

  React.useEffect(() => {
    /* istanbul ignore next */
    if (!storageKey) return;
    const storedItemsPerPage = localStorage.getItem(storageKey);
    if (storedItemsPerPage) {
      const item = defaultOptions.includes(Number(storedItemsPerPage)) ? Number(storedItemsPerPage) : baseItemsPerPage;
      onItemsPerPageChange(item);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, baseItemsPerPage, defaultOptions]);

  const handleFirstPage = React.useCallback(() => {
    onPageChange(1);
  }, [onPageChange]);

  const handleItemsPerPageChange = React.useCallback(
    (newItemsPerPage: number) => {
      onItemsPerPageChange(newItemsPerPage);
      /* istanbul ignore else */
      if (storageKey) {
        localStorage.setItem(storageKey, String(newItemsPerPage));
      }
      handleFirstPage();
    },
    [onItemsPerPageChange, storageKey, handleFirstPage],
  );

  const handlePrevPage = React.useCallback(() => {
    onPageChange(currentPage - 1);
  }, [currentPage, onPageChange]);

  const handleNextPage = React.useCallback(() => {
    onPageChange(currentPage + 1);
  }, [currentPage, onPageChange]);

  const handleLastPage = React.useCallback(() => {
    onPageChange(calculatedLastPage);
  }, [calculatedLastPage, onPageChange]);

  const pageNumbers = React.useMemo(() => {
    const pages = [];
    const adjacentPages = 2;

    for (let i = 1; i <= calculatedLastPage; i += 1) {
      if (i === 1 || i === calculatedLastPage || (i >= currentPage - adjacentPages && i <= currentPage + adjacentPages)) {
        pages.push(
          <Button
            key={i}
            onClick={() => onPageChange(i)}
            disabled={isFetching || currentPage === i}
            variant={currentPage === i ? 'solid' : 'outline'}
            size="xs"
            colorScheme={currentPage === i ? 'blue' : 'unset'}
            data-testid={`page-${i}`}
          >
            {i}
          </Button>,
        );
      } else if (i === currentPage - adjacentPages - 1 || i === currentPage + adjacentPages + 1) {
        pages.push(
          <Text key={i} mx={2}>
            ...
          </Text>,
        );
      }
    }

    return pages;
  }, [calculatedLastPage, currentPage, onPageChange, isFetching]);

  if (total <= perPageItem) {
    return (
      <Box d="flex" justifyContent="flex-start" alignItems="center">
        <HStack>
          <Text>{t('pagination.show')}</Text>
          {currentOptions.length > 1 && (
            /* istanbul ignore next */
            <Select
              value={baseItemsPerPage}
              onChange={
                /* istanbul ignore next */ (e) => {
                  handleItemsPerPageChange(Number(e.target.value));
                }
              }
              disabled={isFetching}
              size="sm"
              borderRadius="md"
              w="auto"
              variant="filled"
              name="select-table-page-limit"
              height="1.5rem"
              px={1}
              data-testid="select-page-limit"
            >
              {currentOptions?.map(
                /* istanbul ignore next */ (option) => (
                  <option value={option} key={option}>
                    {option}
                  </option>
                ),
              )}
            </Select>
          )}
          {currentOptions.length === 1 && <Text>{total ?? /* istanbul ignore next */ 0}</Text>}

          <Text>{t('pagination.info', { total: total ?? /* istanbul ignore next */ 0 })}</Text>
        </HStack>
      </Box>
    );
  }

  return (
    <HStack justifyContent="space-between" w="100%" sx={size === 'large' ? /* istanbul ignore next */ { py: 1, px: 2 } : {}}>
      <HStack>
        <Text>{t('pagination.show')}</Text>
        <Select
          value={baseItemsPerPage}
          onChange={(e) => {
            handleItemsPerPageChange(Number(e.target.value));
          }}
          disabled={isFetching}
          size="sm"
          borderRadius="md"
          w="auto"
          variant="filled"
          name="select-table-page-limit"
          height="1.5rem"
          px={1}
          data-testid="select-page-limit"
        >
          {currentOptions?.map((option) => (
            <option value={option} key={option}>
              {option}
            </option>
          ))}
        </Select>
        <Text>{t('pagination.info', { total })}</Text>
      </HStack>
      <ButtonGroup variant="outline" size="xs" color="gray" padding={0} spacing={1} borderRadius="md">
        <IconButton
          disabled={currentPage === 1 || isFetching}
          variant="ghost"
          onClick={handleFirstPage}
          data-testid="first-page-button"
          aria-label="first page"
          fontSize="20px"
          icon={<MdFirstPage />}
        />
        <IconButton
          disabled={currentPage === 1 || isFetching}
          variant="ghost"
          onClick={handlePrevPage}
          data-testid="prev-page-button"
          aria-label="previous page"
          fontSize="16px"
          icon={<IoChevronBack />}
        />
        {pageNumbers}
        <IconButton
          disabled={currentPage === calculatedLastPage || isFetching}
          variant="ghost"
          onClick={handleNextPage}
          data-testid="next-page-button"
          aria-label="next page"
          fontSize="16px"
          icon={<IoChevronForward />}
        />
        <IconButton
          disabled={currentPage === calculatedLastPage || isFetching}
          variant="ghost"
          onClick={handleLastPage}
          data-testid="last-page-button"
          aria-label="last page"
          fontSize="20px"
          icon={<MdLastPage />}
        />
      </ButtonGroup>
    </HStack>
  );
};

export default Pagination;
